/**
 * A repository, read (§15) — what its own files say about running it.
 *
 * One project is one git repository, and every field here is a **prefill**: the
 * user edits it afterwards, in the app's workspace settings or on the account
 * area's project form. Nothing here decides anything; it only saves somebody
 * typing what the repository already says.
 *
 * Shared between the two surfaces that point at a repository — the app, on a
 * folder of the machine, and the account area, on a folder somebody grants their
 * browser (§5.25). It reads through `RepoReader` and knows nothing else: no
 * absolute path, no `fs`, no `git`. The two things that genuinely need those —
 * finding the repositories under a picked folder, and asking which branch is
 * checked out — stay with each surface.
 */
import { detectKind } from './detect-kind';
import { describePhp } from './detect-php';
import { detectEnv, dotenvPort, type DetectedEnvVar } from './detect-env';
import { namesIn, readJson, type RepoReader } from './detect-read';
import { planEnvInjection, resolveEnvFile } from './env';
import { kindServes, type EnvFileMode, type ProjectKind, type RuntimeKind } from './vocabulary';

/**
 * Everything a repository answers for itself.
 *
 * Three fields of a project are deliberately absent, because no repository
 * records them: the domain reserved with a tunnel provider and whether a port is
 * published are decisions a person takes (§5.17), and the name of the variable a
 * project reads its port from is the user's own field (§5.19).
 */
export interface DetectedRepo {
  runtime: RuntimeKind;
  kind: ProjectKind;
  packageManager: string | null;
  installCmd: string | null;
  startCmd: string | null;
  /** What proves the repository still works — a workflow step runs it (§5.15). */
  checkCmd: string | null;
  healthCheck: string | null;
  /** `:3000` — stored with the colon, as a project's port is everywhere. */
  port: string | null;
  envFile: EnvFileMode;
  env: DetectedEnvVar[];
}

/**
 * Resolve a Gradle project's dependencies without building it — the counterpart
 * of Maven's `dependency:go-offline`, and the reason it is this long.
 *
 * **`gradle dependencies` is not enough.** It resolves the dependency *graph*,
 * which downloads POMs and `.module` metadata and stops there; Gradle resolves
 * artifacts separately, and the jars are the bytes. The only way to pull them
 * without building is to resolve every resolvable configuration by hand, which
 * is what the `projectsEvaluated` hook below does — `p.configurations` covers
 * `compileClasspath`, `runtimeClasspath`, `testRuntimeClasspath` and whatever a
 * plugin added, across every project in the build.
 *
 * **Injected, not committed.** `-I` applies an init script to this invocation
 * only: nothing is written into the checkout, so nothing lands in the review
 * (§6.3), and the project's own build files are untouched.
 *
 * **Written to `$HOME`, inline.** Not baked into the image, because a session
 * can run on the host with no image at all (§5.19) and a Dockerfile is the
 * user's to edit — an install command that depends on a layer being present is
 * one a custom base breaks. Self-contained means it also works when someone
 * copies it out of the settings field and runs it by hand.
 *
 * `try`/`catch` per configuration on purpose: a configuration that cannot
 * resolve — a variant with unsatisfiable attributes, a platform with no
 * artifacts — is normal in a large build and must not fail the install, which
 * would block Start (§5.11) and withhold the shared cache
 * (`uncacheableRuntimes`). Whatever did resolve is in `~/.gradle` either way.
 *
 * `help` is the cheapest built-in task: it configures the build, which is what
 * fires the hook, and then does nothing.
 */
const GRADLE_INIT = [
  'gradle.projectsEvaluated {',
  '  gradle.rootProject.allprojects { p ->',
  '    p.configurations.each { c ->',
  '      if (c.canBeResolved) { try { c.resolve() } catch (Throwable t) { } }',
  '    }',
  '  }',
  '}'
].join(' ');

const GRADLE_RESOLVE_SCRIPT = '"$HOME/.airlock-gradle-resolve.gradle"';

const GRADLE_RESOLVE =
  `printf '%s\\n' '${GRADLE_INIT}' > ${GRADLE_RESOLVE_SCRIPT} && ` + `./gradlew -I ${GRADLE_RESOLVE_SCRIPT} -q help`;

/**
 * A framework's own default port, used only when the repository does not say.
 *
 * Nothing in a Next.js or FastAPI repository writes 3000 or 8000 down — the
 * framework supplies it — so reading files alone leaves the port empty, and an
 * empty port is what stops Preview from ever reaching the project (§5.11). These
 * are the documented defaults, and the user edits them like any other prefill.
 */
const NODE_FRAMEWORK_PORTS: [RegExp, number][] = [
  [/"(next)"\s*:/, 3000],
  [/"nuxt"\s*:/, 3000],
  [/"@remix-run\//, 3000],
  [/"react-scripts"\s*:/, 3000],
  [/"@angular\/core"\s*:/, 4200],
  [/"astro"\s*:/, 4321],
  [/"vite"\s*:/, 5173],
  [/"@sveltejs\/kit"\s*:/, 5173],
  [/"express"\s*:/, 3000],
  [/"fastify"\s*:/, 3000],
  [/"@nestjs\/core"\s*:/, 3000]
];

/** The JVM config files a port is written in, when the build system does not say. */
const JVM_PORT_FILES = ['src/main/resources/application.properties', 'src/main/resources/application.yml'];

/**
 * `within` is set for a folder of a repository that declares a JavaScript
 * workspace (§5.4, `detect-members.ts`): its dependencies are installed once, at
 * the top, by the package manager the top's lockfile names — so a folder with no
 * lockfile of its own runs its scripts with that one and installs nothing itself.
 */
export async function describeRepo(reader: RepoReader, within?: { packageManager: string }): Promise<DetectedRepo> {
  const files = await namesIn(reader, '');
  // The repository's own environment, read once: it prefills the project's env
  // vars and, highest priority across every ecosystem, the port — one the
  // developer configured beats one a framework defaults to.
  const env = await detectEnv(reader);
  const configured = dotenvPort(env);
  let runtime: RuntimeKind = 'unknown';
  let packageManager: string | null = null;
  let installCmd: string | null = null;
  let startCmd: string | null = null;
  let checkCmd: string | null = null;
  let port: string | null = null;

  if (files.has('package.json')) {
    runtime = 'node';
    const own = files.has('pnpm-lock.yaml')
      ? 'pnpm'
      : files.has('yarn.lock')
        ? 'yarn'
        : files.has('bun.lockb') || files.has('bun.lock')
          ? 'bun'
          : files.has('package-lock.json')
            ? 'npm'
            : null;
    packageManager = own ?? within?.packageManager ?? 'npm';
    installCmd = own || !within ? `${packageManager} install` : null;
    const raw = (await reader.read('package.json')) ?? '';
    const pkg = await readJson(reader, 'package.json');
    const scripts = (pkg?.scripts ?? {}) as Record<string, string>;
    const script = ['dev', 'start', 'serve'].find((one) => scripts[one]);
    if (script) startCmd = packageManager === 'npm' ? `npm run ${script}` : `${packageManager} ${script}`;
    // The repository's own word for "is this still good": a `check` script that
    // gathers lint and tests beats guessing at `test` when the project has both.
    const gate = ['check', 'ci', 'verify', 'test'].find((one) => scripts[one]);
    if (gate) checkCmd = packageManager === 'npm' ? `npm run ${gate}` : `${packageManager} ${gate}`;
    // What the project wrote wins over what its framework defaults to.
    const runner = await runnerPort(reader, script ? scripts[script] : '');
    port =
      configured ??
      guessPort(Object.values(scripts).join(' ')) ??
      (runner ? runner.port : frameworkPort(raw));
  } else if (files.has('pom.xml')) {
    runtime = 'java';
    // Dependencies, not a build. This was `-DskipTests package`, which compiles
    // the whole project — and for the JVM that is the one shape of install the
    // caches cannot help with: `DEP_DIRS` has no java entry because Maven puts
    // nothing in the repository, so `target/` is destroyed by every clone and
    // carried by nothing (§8.2/§8.6). The compile was re-run in full on every
    // boot and left nothing behind, while the part that *is* cacheable — the
    // artifacts under `~/.m2` — is exactly what `go-offline` resolves.
    //
    // It also decides what an install *failing* means. `package` fails on a
    // compile error, which is a fact about the branch and not about its
    // dependencies, and a failed install blocks Start (§5.11) and now withholds
    // the shared cache (`uncacheableRuntimes`). Resolving alone fails only when
    // a dependency genuinely cannot be fetched. The compile moves to the first
    // Run, where `spring-boot:run` does it anyway and reports it as itself.
    //
    // `go-offline` resolves plugins and reports as well as dependencies. Its
    // known gap is the artifact a plugin fetches lazily at execution time, which
    // no resolve-only goal can see; that one downloads on first Run and lands in
    // the same `~/.m2` (§8.2).
    //
    // `-T 1C` resolves independent modules in parallel, one thread per core. A
    // multi-module Spring project is the ordinary case and the default is
    // strictly serial, which leaves most of a session's vCPUs idle through the
    // longest phase of its boot (§8.3).
    //
    // Not `-o`: the *first* resolve has to reach the network. Offline is added
    // by the runner once a cache is in place, where it is safe (§8.4).
    installCmd = 'mvn -q -T 1C dependency:go-offline';
    startCmd = 'mvn spring-boot:run';
    // The wrapper when the repository committed one: it pins the build tool the
    // project was written against, and it is one less thing the image has to
    // carry (§10.1).
    checkCmd = files.has('mvnw') ? './mvnw -q test' : 'mvn -q test';
    port = configured ?? (await guessPortIn(reader, JVM_PORT_FILES)) ?? ':8080';
  } else if (files.has('build.gradle') || files.has('build.gradle.kts')) {
    runtime = 'java';
    installCmd = GRADLE_RESOLVE;
    startCmd = './gradlew bootRun';
    checkCmd = files.has('gradlew') ? './gradlew test' : 'gradle test';
    port = configured ?? (await guessPortIn(reader, JVM_PORT_FILES)) ?? ':8080';
  } else if (files.has('pyproject.toml') || files.has('requirements.txt') || files.has('manage.py')) {
    runtime = 'python';
    ({ installCmd, startCmd, checkCmd, port } = await describePython(reader, files, configured));
  } else if (files.has('go.mod')) {
    runtime = 'go';
    installCmd = 'go mod download';
    startCmd = 'go run ./...';
    checkCmd = 'go test ./...';
    port = configured ?? (await guessPortIn(reader, ['main.go', 'cmd/server/main.go', 'cmd/api/main.go', 'server.go']));
  } else if (files.has('composer.json')) {
    runtime = 'php';
    packageManager = 'composer';
    ({ installCmd, startCmd, checkCmd, port } = await describePhp(reader, files, configured, guessPort));
  } else if (files.has('Cargo.toml')) {
    runtime = 'rust';
    installCmd = 'cargo fetch';
    startCmd = 'cargo run';
    checkCmd = 'cargo test';
    port = configured ?? (await guessPortIn(reader, ['src/main.rs']));
  } else if (files.has('index.html')) {
    runtime = 'static';
  }

  // Whether anything here is meant to be started at all — the last question the
  // classifier asks, so it has to be answered first.
  const kind = await detectKind(reader, runtime, files, Boolean(port ?? startCmd));
  return {
    runtime,
    kind,
    packageManager,
    installCmd,
    startCmd,
    checkCmd,
    healthCheck: healthProbe(kind, port),
    port,
    envFile: await detectEnvFile(reader, runtime, files),
    env
  };
}

/**
 * Which channel this project's environment travels by (§5.7) — a `.env` the
 * technology reads, or the process environment alone. Read here because this is
 * the one place with the repository in front of it: the answer turns on
 * dependencies (`dotenvy`, `godotenv`, Quarkus) and on a committed template,
 * neither of which survives into the sandbox as a fact about the project.
 *
 * `auto` wherever the ecosystem's own convention already gives that answer, so
 * the stored mode stays a *convention* rather than an assertion. The difference
 * matters at boot: a concrete mode is what overrules the refusal to write a
 * dotenv `.gitignore` does not name, and only the repository saying so itself —
 * a loader in its manifest, a committed template, an ecosystem `RuntimeKind`
 * cannot name — earns that. A prefill either way; settings overrules it.
 */
async function detectEnvFile(reader: RepoReader, runtime: RuntimeKind, files: Set<string>): Promise<EnvFileMode> {
  const manifest = await readManifest(reader, runtime, files);
  const read = planEnvInjection(runtime, { files: [...files], manifest }).dotenv;
  if (read === resolveEnvFile('auto', runtime).dotenv) return 'auto';
  return read ? 'dotenv' : 'none';
}

/**
 * Where a project declares its dependencies. Only the ecosystems whose dotenv
 * answer depends on them are listed; for the rest the convention decides and
 * reading the manifest would buy nothing.
 */
const DEP_MANIFESTS: Partial<Record<RuntimeKind, readonly string[]>> = {
  java: ['pom.xml', 'build.gradle', 'build.gradle.kts'],
  go: ['go.mod'],
  rust: ['Cargo.toml'],
  php: ['composer.json']
};

async function readManifest(reader: RepoReader, runtime: RuntimeKind, files: Set<string>): Promise<string> {
  // No runtime of its own means it may still be a .NET project, whose manifest
  // is named after the assembly rather than after the build system (§5.7).
  const named = DEP_MANIFESTS[runtime] ?? [...files].filter((file) => /\.(csproj|fsproj|vbproj)$/i.test(file));
  const texts = await Promise.all(named.filter((file) => files.has(file)).map((file) => reader.read(file)));
  return texts.filter(Boolean).join('\n');
}

/**
 * A readiness probe, proposed only where the repository already tells us enough
 * for one to be right (§5.11) — `curl` is in the session image, so it is the
 * probe every runtime can share.
 *
 * Only for a project that serves pages: its root answering 200 is what "it is
 * up" means. An API's root is routinely a 404 and its health route is spelled
 * `/health`, `/healthz`, `/actuator/health` or nothing at all — guessing leaves
 * the dock on `starting` forever, which is worse than the no probe it has now.
 */
function healthProbe(kind: ProjectKind, port: string | null): string | null {
  return kindServes(kind).page && port ? `curl -fsS -o /dev/null http://localhost${port}` : null;
}

/**
 * Python has no equivalent of `package.json` `scripts`, so the run command has
 * to come from the framework in the dependency list — which is why this used to
 * be left empty and a Python project could be added but never started.
 */
async function describePython(
  reader: RepoReader,
  files: Set<string>,
  /** A port the repository configured; the run command has to carry the same one. */
  configured: string | null
): Promise<{ installCmd: string | null; startCmd: string | null; checkCmd: string | null; port: string | null }> {
  const pyproject = (await reader.read('pyproject.toml')) ?? '';
  const requirements = (await reader.read('requirements.txt')) ?? '';
  const deps = `${pyproject}\n${requirements}`.toLowerCase();

  const uv = files.has('uv.lock') || /\[tool\.uv\b/.test(pyproject);
  const poetry = /\[tool\.poetry\b/.test(pyproject);
  // `uv sync` only works on a uv project; running it on a plain pyproject is an
  // error, and on a requirements.txt repository there is nothing for it to read.
  const installCmd = uv
    ? 'uv sync'
    : poetry
      ? 'poetry install'
      : files.has('requirements.txt')
        ? 'pip install -r requirements.txt'
        : 'pip install -e .';

  // Both of those install into an environment of their own, so the runner has to
  // be reached through them — a bare `pytest` after `uv sync` is not on PATH.
  const through = uv ? 'uv run ' : poetry ? 'poetry run ' : '';
  const pytest = /\bpytest\b/.test(deps) || files.has('pytest.ini') || files.has('conftest.py');
  const checkCmd = files.has('manage.py')
    ? `${through}python manage.py test`
    : pytest
      ? `${through}pytest`
      : // The standard library's runner, which needs nothing installed — but only
        // where there is something for it to discover. Python is the one
        // ecosystem here whose test runner is not implied by the manifest, so a
        // repository that names none is left with no check rather than a
        // plausible command that fails on the first run (§15).
        files.has('tests') || files.has('test')
        ? `${through}python -m unittest discover`
        : null;

  // The command spells the port out, so it must be the one reported alongside
  // it: Preview publishes the field, and a server listening elsewhere is a
  // mapping that never answers (§5.11).
  const on = (fallback: number): number => Number((configured ?? `:${fallback}`).slice(1));

  if (files.has('manage.py')) {
    const at = on(8000);
    return { installCmd, startCmd: `python manage.py runserver 0.0.0.0:${at}`, checkCmd, port: `:${at}` };
  }
  if (/\bfastapi\b/.test(deps)) {
    const target = (await findAsgiApp(reader)) ?? 'main:app';
    const at = on(8000);
    return { installCmd, startCmd: `uvicorn ${target} --host 0.0.0.0 --port ${at}`, checkCmd, port: `:${at}` };
  }
  if (/\bflask\b/.test(deps)) {
    const at = on(5000);
    return { installCmd, startCmd: `flask run --host 0.0.0.0 --port ${at}`, checkCmd, port: `:${at}` };
  }
  return { installCmd, startCmd: null, checkCmd, port: configured };
}

/**
 * `uvicorn` needs `module:variable`, and getting it wrong is the difference
 * between a server and a stack trace — so the module is the one that actually
 * builds the app, found by reading the usual entry points rather than guessed.
 */
async function findAsgiApp(reader: RepoReader): Promise<string | null> {
  const candidates = ['main.py', 'app.py', 'app/main.py', 'src/main.py', 'api/main.py', 'app/__init__.py'];
  for (const rel of candidates) {
    const source = await reader.read(rel);
    const variable = source && /^\s*(\w+)\s*=\s*FastAPI\s*\(/m.exec(source)?.[1];
    if (!variable) continue;
    const module = rel
      .replace(/\.py$/, '')
      .replace(/\/__init__$/, '')
      .split('/')
      .join('.');
    return `${module}:${variable}`;
  }
  return null;
}

/** The first port a framework's dependency implies, when the repo names none. */
function frameworkPort(packageJson: string): string | null {
  for (const [marker, value] of NODE_FRAMEWORK_PORTS) if (marker.test(packageJson)) return `:${value}`;
  return null;
}

/**
 * The port the run command's own tool decides, read where that tool keeps it —
 * null when the command is not one of them, so the framework default applies.
 *
 * `wrangler dev` serves on 8787 whatever the bundler underneath is, unless its
 * config's `[dev]` says otherwise; vite's `server.port` is where a monorepo
 * keeps its folders off each other's 5173; and an Electron app opens a window,
 * not a port anybody reaches — its `{ port: null }` stops vite's default from
 * being proposed for it.
 */
async function runnerPort(reader: RepoReader, run: string): Promise<{ port: string | null } | null> {
  if (/\bwrangler\s+(dev|pages\s+dev)\b/.test(run)) {
    const toml = (await reader.read('wrangler.toml')) ?? '';
    const dev = /^\[dev\][^[]*?^\s*port\s*=\s*(\d{2,5})/ms.exec(toml);
    return { port: `:${dev?.[1] ?? '8787'}` };
  }
  if (/\belectron(-vite|-forge)?\b/.test(run)) return { port: null };
  if (/\bvite\b/.test(run)) {
    for (const name of ['vite.config.ts', 'vite.config.mts', 'vite.config.js', 'vite.config.mjs']) {
      const config = await reader.read(name);
      const found = config && /\bserver\s*:\s*\{[^}]*?\bport\s*:\s*(\d{2,5})/s.exec(config);
      if (found) return { port: `:${found[1]}` };
    }
  }
  return null;
}

/** Read a few likely entry points and take the first port written down. */
async function guessPortIn(reader: RepoReader, relatives: string[]): Promise<string | null> {
  for (const rel of relatives) {
    const source = await reader.read(rel);
    const found = source && guessPort(source);
    if (found) return found;
  }
  return null;
}

function guessPort(text: string): string | null {
  const found = /--port[= ](\d{2,5})|\bport[=:]\s*"?(\d{4,5})\b|:(\d{4,5})\b/i.exec(text);
  const port = found?.[1] ?? found?.[2] ?? found?.[3];
  return port ? `:${port}` : null;
}
